/**
 * Shared channel between `commands.js` (context-menu handlers) and the task
 * pane. The command bundle and the task pane bundle run in separate iframes
 * but share the same origin → they share `localStorage`, and writes from one
 * fire `storage` events in the other.
 *
 * A context-menu click writes a PendingIntent here; the task pane consumes
 * it (from a storage event if open, or on mount after being opened).
 */

/* global localStorage, Date */

const STORAGE_KEY = 'signus.pendingIntent';
/** Intents older than this are treated as stale and ignored. */
const MAX_AGE_MS = 15_000;

export interface PendingIntent {
  type: 'redline';
  editTypeId: string;
  timestamp: number;
}

export function writePendingIntent(intent: Omit<PendingIntent, 'timestamp'>): void {
  const full: PendingIntent = { ...intent, timestamp: Date.now() };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(full));
  } catch {
    // Quota / privacy mode — swallow.
  }
}

export function readPendingIntent(): PendingIntent | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PendingIntent;
    if (!parsed?.editTypeId || typeof parsed.timestamp !== 'number') return null;
    if (Date.now() - parsed.timestamp > MAX_AGE_MS) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function clearPendingIntent(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // noop
  }
}

export const PENDING_INTENT_STORAGE_KEY = STORAGE_KEY;
